// ============================================================================
// LIKEPOPOVER - Popover del corazón: quién ha dado like (foto + nombre)
// ============================================================================
// Envuelve el botón del corazón. Al pasar el ratón (o enfocar), despliega una
// tarjeta con la foto/iniciales y el nombre de cada persona que ha dado like.
// Se pinta con portal a <body> y posición FIJA calculada con getBoundingClientRect,
// para que el overflow del hilo de comentarios no lo recorte. Si no cabe debajo
// del corazón, se voltea hacia arriba (flip). Es informativo (pointer-events:none):
// no hace falta que el ratón entre en él, así que se muestra/oculta con el hover
// del corazón.
// ============================================================================

import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Avatar from './Avatar';
import '../styles/like-popover.css';

const POP_MAX_W = 240;
const MAX_SHOWN = 8; // como el popover es pointer-events:none no se puede scrollear
const GAP = 8;
const MARGIN = 8;

function LikePopover({ people = [], extra = 0, children, className = '' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: 'below' });
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const rectRef = useRef(null);

  const show = useCallback(() => {
    if (!people.length) return;
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    rectRef.current = r;
    const half = POP_MAX_W / 2;
    let cx = r.left + r.width / 2;
    cx = Math.max(half + MARGIN, Math.min(cx, window.innerWidth - half - MARGIN));
    setPos({ top: r.bottom + GAP, left: cx, placement: 'below' });
    setOpen(true);
  }, [people.length]);

  const hide = useCallback(() => setOpen(false), []);

  // Tras render (antes de pintar): si no cabe debajo pero sí encima, voltea arriba.
  useLayoutEffect(() => {
    if (!open) return;
    const pop = popRef.current;
    const r = rectRef.current;
    if (!pop || !r) return;
    const h = pop.offsetHeight;
    const fitsBelow = r.bottom + GAP + h <= window.innerHeight - MARGIN;
    const fitsAbove = r.top - GAP - h >= MARGIN;
    if (!fitsBelow && fitsAbove) {
      setPos((p) => (p.placement === 'above' ? p : { ...p, top: r.top - GAP - h, placement: 'above' }));
    }
  }, [open, people.length]);

  // Si el usuario hace scroll o redimensiona con el popover abierto, el ancla se
  // mueve: lo ocultamos (es un tooltip efímero, no un menú anclado).
  useEffect(() => {
    if (!open) return;
    const onLayout = () => hide();
    window.addEventListener('scroll', onLayout, true);
    window.addEventListener('resize', onLayout);
    return () => {
      window.removeEventListener('scroll', onLayout, true);
      window.removeEventListener('resize', onLayout);
    };
  }, [open, hide]);

  const shown = people.slice(0, MAX_SHOWN);
  const more = extra + Math.max(0, people.length - shown.length);

  return (
    <span
      ref={anchorRef}
      className={`like-pop-anchor ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open && people.length > 0 && createPortal(
        <div
          ref={popRef}
          className={`like-pop like-pop--${pos.placement}`}
          style={{ top: pos.top, left: pos.left, maxWidth: POP_MAX_W }}
          role="tooltip"
        >
          <span className="like-pop__arrow" aria-hidden="true" />
          <div className="like-pop__head">
            {people.length === 1 ? 'Le gusta a' : 'Les gusta a'}
          </div>
          <ul className="like-pop__list">
            {shown.map((p, i) => (
              <li key={p.id ?? i} className="like-pop__item">
                <Avatar name={p.nombre} src={p.avatar} size={22} title="" />
                <span className="like-pop__name">{p.nombre}</span>
              </li>
            ))}
            {more > 0 && <li className="like-pop__more">y {more} más</li>}
          </ul>
        </div>,
        document.body
      )}
    </span>
  );
}

export default LikePopover;
